export type AppliedFilterSource = "billsIncluded" | "amenities" | "roomType";

export type AppliedFilterSequenceItem = {
  source: AppliedFilterSource;
  value: string;
};

export const APPLIED_FILTER_SOURCES: AppliedFilterSource[] = [
  "billsIncluded",
  "amenities",
  "roomType",
];

export const toFilterArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).map(String);
  }
  if (typeof value === "string" && value) {
    return value.split(",").filter(Boolean);
  }
  return [];
};

const FILTER_KEY_TO_SOURCE: Record<string, AppliedFilterSource> = {
  billsIncluded: "billsIncluded",
  bills_included: "billsIncluded",
  amenities: "amenities",
  roomType: "roomType",
  meta_types: "roomType",
  room_types: "roomType",
};

export const getInScopeFilterValues = (filters: any, source: AppliedFilterSource): string[] => {
  switch (source) {
    case "billsIncluded":
      return toFilterArray(filters?.billsIncluded ?? filters?.bills_included);
    case "amenities":
      return toFilterArray(filters?.amenities);
    case "roomType":
      return toFilterArray(filters?.roomType ?? filters?.meta_types ?? filters?.room_types);
    default:
      return [];
  }
};

// Walks keys in object order, then comma/array order within each key.
// That matches the query string on reload (`?bills_included=wifi&amenities=gym&meta_types=ensuite`).
export const buildAppliedFilterSequenceFromQuery = (
  query: Record<string, unknown> | null | undefined,
): AppliedFilterSequenceItem[] => {
  if (!query) return [];
  const sequence: AppliedFilterSequenceItem[] = [];
  const seen = new Set<string>();

  Object.keys(query).forEach((key) => {
    const source = FILTER_KEY_TO_SOURCE[key];
    if (!source) return;
    toFilterArray(query[key]).forEach((value) => {
      const id = `${source}:${value}`;
      if (seen.has(id)) return;
      seen.add(id);
      sequence.push({ source, value });
    });
  });

  return sequence;
};

export const buildAppliedFilterSequenceFromFilters = (filters: any): AppliedFilterSequenceItem[] =>
  buildAppliedFilterSequenceFromQuery(filters);

export const updateAppliedFilterSequence = (
  prevSequence: AppliedFilterSequenceItem[] = [],
  nextFilters: any,
  // Filters already on the page. Used when the stored sequence is empty so a
  // new selection is appended instead of rebuilding in group order.
  previousFilters?: any,
): AppliedFilterSequenceItem[] => {
  const nextBySource = APPLIED_FILTER_SOURCES.reduce((acc, source) => {
    acc[source] = getInScopeFilterValues(nextFilters, source);
    return acc;
  }, {} as Record<AppliedFilterSource, string[]>);

  const baseSequence = prevSequence?.length
    ? prevSequence
    : buildAppliedFilterSequenceFromQuery(previousFilters);

  const nextSequence = (baseSequence || []).filter((item) =>
    nextBySource[item.source]?.includes(item.value),
  );
  const existing = new Set(nextSequence.map((item) => `${item.source}:${item.value}`));

  APPLIED_FILTER_SOURCES.forEach((source) => {
    nextBySource[source].forEach((value) => {
      const key = `${source}:${value}`;
      if (!existing.has(key)) {
        nextSequence.push({ source, value });
        existing.add(key);
      }
    });
  });

  return nextSequence;
};

export const resolveAppliedFilterSequence = (
  sequence: AppliedFilterSequenceItem[] | undefined,
  filters: any,
): AppliedFilterSequenceItem[] => {
  const selected = APPLIED_FILTER_SOURCES.reduce((acc, source) => {
    acc[source] = new Set(getInScopeFilterValues(filters, source));
    return acc;
  }, {} as Record<AppliedFilterSource, Set<string>>);

  const fromSession = (sequence || []).filter((item) => selected[item.source]?.has(item.value));
  return fromSession.length ? fromSession : buildAppliedFilterSequenceFromFilters(filters);
};
